import { Injectable } from '@angular/core';
import { Project } from '../../models/project';
import { DbService } from '../db/db.service';
import { PermissionsEnum } from '../../enums/permissions.enum';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../config/config.service';
import { isNoEmptyArray, isNullOrUndefined } from '@pineapplelab/util';
import { TermsAndConditions } from '../../models/termsAndConditions';
import { TranslateService } from '@ngx-translate/core';
import firebase from "firebase/compat/app";
import { Legal } from '../../models/legal';

@Injectable({
	providedIn: 'root'
})
export class ProjectService {

	projectPermissions: PermissionsEnum[] = [];
	logo: string;

	constructor(
		private db: DbService,
		private configService: ConfigService,
		private translateService: TranslateService
	) { }

	private getLegalData(collection: string): firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
		if (!collection) {
			return null;
		}

		return this.db.getProyIdRef(environment.projectId).collection(Legal.PATH).doc(Legal.DATA_PATH).collection(collection);
	}

	async getProjectData(projectId: string) {
		const project = await this.db.getProyIdRef(projectId).get().then(doc => {
			if (doc.exists) {
				const project = new Project(doc.data());
				this.logo = project.logo;
				this.projectPermissions = project.permissions;
				return project.permissions;
			}
			return null;
		});
		return project;
	}

	async getPermissions(): Promise<PermissionsEnum[]> {
		const results = await this.db.getProyIdRef(environment.projectId).get();
		if (!results.exists) {
			return [];
		}
		return (results.data() as any)?.permissions as PermissionsEnum[];
	}

	async saveProjectPermissions(permissions: PermissionsEnum[]): Promise<void> {
		if (!permissions) {
			return;
		}

		if (!permissions.every(p => typeof p === 'number' && PermissionsEnum[p])) {
			return;
		}

		await this.db.getProyIdRef(environment.projectId).set({ permissions }, { merge: true });
	}

	hasPermissions(permission: PermissionsEnum | PermissionsEnum[], inclusive: boolean = false): boolean {
		const { permissions: permissionsList } = this.configService.getConfig();

		if (!isNoEmptyArray(permissionsList) || isNullOrUndefined(permission)) {
			return false;
		}

		if (!Array.isArray(permission)) {
			return permissionsList.includes(permission);
		}

		return inclusive
			? permission.every(p => permissionsList.includes(p))
			: permission.some(p => permissionsList.includes(p));
	}

	getUniqueClientUsername(): string {
		return this.configService.getConfig()?.uniqueClientUsername;
	}

	async getLegalDocumentData(collection: string, language: string, field: string = 'text'): Promise<string> {
		if (!collection) {
			return null;
		}
		if (!language) {
			language = this.translateService.currentLang ?? this.translateService.defaultLang ?? 'en';
		}
		const doc = await this.getLegalData(collection).doc(language).get();
		if (!doc.exists) {
			return null;
		}
		return doc.data()?.[field];
	}

}
